import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const CART_DRAWER_SAFE_AREA_STYLE_ID = 'gq-cart-drawer-safe-area';

if (!document.getElementById(CART_DRAWER_SAFE_AREA_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = CART_DRAWER_SAFE_AREA_STYLE_ID;
  style.textContent = `
    .cart-drawer .gq-cart-item-type {
      margin: 4px 0 0;
      font-size: 0.72rem;
      line-height: 1.35;
      font-weight: 700;
      opacity: 0.82;
    }

    @media screen and (max-width: 749px) {
      .cart-drawer__summary {
        padding-bottom: calc(var(--cart-drawer-summary-padding) + env(safe-area-inset-bottom, 0px));
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #cartContentObserver = null;

  /** @type {number | null} */
  #cartLabelTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleCartItemLabels);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#observeCartContent();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleCartItemLabels);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#cartContentObserver?.disconnect();
    this.#cartContentObserver = null;
    if (this.#cartLabelTimer !== null) {
      window.clearTimeout(this.#cartLabelTimer);
      this.#cartLabelTimer = null;
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    this.showDialog();
    this.#announceCartCount(event.detail.resource?.item_count);
    this.#scheduleCartItemLabels();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Watches cart drawer section morphs and quantity changes so delivery labels stay accurate.
   */
  #observeCartContent() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    if (!this.#cartContentObserver) {
      this.#cartContentObserver = new MutationObserver(() => {
        if (!dialog.open) return;
        this.#scheduleCartItemLabels();
      });
    }

    this.#cartContentObserver.observe(dialog, { childList: true, subtree: true });
  }

  #scheduleCartItemLabels = () => {
    if (this.#cartLabelTimer !== null) {
      window.clearTimeout(this.#cartLabelTimer);
    }

    this.#cartLabelTimer = window.setTimeout(() => {
      this.#cartLabelTimer = null;
      this.#applyCartItemLabels();
    }, 100);
  };

  /**
   * @param {Record<string, any>} item
   * @returns {string}
   */
  #getCartItemLabel(item) {
    const isSpanish = (document.documentElement.lang || '').toLowerCase().startsWith('es');
    const handle = String(item.handle || '').toLowerCase();
    const productType = String(item.product_type || '').toLowerCase();
    const variantTitle = String(item.variant_title || '').toLowerCase();
    const requiresShipping = Boolean(item.requires_shipping);

    if (handle === 'glossariq-road-ready-ai-membership' || productType === 'digital membership') {
      return isSpanish ? 'Membresía mensual • Sin envío' : 'Monthly membership • No shipping';
    }

    if (productType === 'physical + digital bundle') {
      return isSpanish
        ? 'Paquete físico + digital • El artículo físico se envía'
        : 'Physical + digital bundle • Physical item ships';
    }

    if (!requiresShipping) {
      const isInteractive =
        productType.includes('interactive') ||
        variantTitle.includes('interactive') ||
        variantTitle.includes('interactivo') ||
        handle === 'road-ready-adventure-club-interactive' ||
        handle === 'road-ready-destinations-interactive';

      if (isInteractive) {
        return isSpanish ? 'Acceso digital • Sin envío' : 'Digital access • No shipping';
      }

      return isSpanish ? 'Artículo digital • Sin envío' : 'Digital item • No shipping';
    }

    return '';
  }

  async #applyCartItemLabels() {
    const dialog = this.refs.dialog;
    if (!dialog?.open) return;

    const rows = dialog.querySelectorAll('tr.cart-items__table-row[data-key]');
    if (!rows.length) return;

    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = `${root.endsWith('/') ? root : `${root}/`}cart.js`;

    try {
      const response = await fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!this.isConnected || !this.refs.dialog) return;

      // Avoid observing the labels we add ourselves, then resume watching cart updates.
      this.#cartContentObserver?.disconnect();
      try {
        this.refs.dialog.querySelectorAll('tr.cart-items__table-row[data-key]').forEach((row) => {
          const key = row.getAttribute('data-key') || '';
          const item = items.find((cartItem) => cartItem.key === key);
          const info = row.querySelector('.cart-items__product-info');
          const existing = row.querySelector('.gq-cart-item-type');
          const label = item ? this.#getCartItemLabel(item) : '';

          if (!info || !label) {
            existing?.remove();
            return;
          }

          if (existing) {
            if (existing.textContent !== label) existing.textContent = label;
            return;
          }

          const note = document.createElement('p');
          note.className = 'gq-cart-item-type';
          note.textContent = label;
          info.appendChild(note);
        });
      } finally {
        this.#observeCartContent();
      }
    } catch (_error) {
      // Delivery labels are a progressive enhancement; cart functionality must never depend on them.
    }
  }

  open() {
    this.showDialog();
    this.#scheduleCartItemLabels();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
